import Dropdown from '../../common/dropdown/Dropdown';
import { arrowLeft } from '../../assets/images/login';

import { Colors, baseUrl_, timeout } from '../../utils/constants';
import { ClipLoader } from 'react-spinners';
import { encryptData } from '../../utils/encryptJson';
import { registerNetcoreActivity } from '../../utils/registerActivity';
import jwtDecode from 'jwt-decode';
import ReactGA from 'react-ga4';
import { Box, Flex, Img, Input, Text } from '@chakra-ui/react';
import Typography from '../../components_/Typography';
import Button from '../../components_/button';
import { useGetTbhSecurityQuestions, useSetUserSecurityQuestions } from '../../apis/auth';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { VALIDATIONS } from '../../utils/validations';
import { useNavigate } from 'react-router-dom';
import { decryptJson } from '../../utils/decryptJson';
import { useEffect, useMemo, useState } from 'react';
import { wqkgx } from '../../config/keys';
import customToast, { ToastType } from '../../components_/Toast';
import CryptoJS from 'crypto-js';
import SuccessModal from '../../components/global/SuccessModal';
import { isEmailValid } from '../../utils/validateEmail';
import { validateEmail } from '../../lib/validateEmail';

const SetSecurityQuestions = ({ userEmail = '', loginToken = '', hasBack = true }) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const passwordEmail = queryParams?.get('source') ?? '';
  const passwordToken = queryParams?.get('token') ?? '';
  const action = queryParams?.get('action') ?? '';

  const signupToken = localStorage.getItem('signup-token');
  const signupEmail = localStorage.getItem('signup-email');
  const token: any = passwordToken || loginToken || signupToken;
  const email: any = passwordEmail || userEmail || signupEmail;
  const details: any = jwtDecode(token);
  const { data: tbhSecurityQuestions, isPending: tbhSecurityQuestionsLoading } =
    useGetTbhSecurityQuestions();
  const { mutate: setUsersSecurityQuestions, isPending: settingUsersQuestionLoading } =
    useSetUserSecurityQuestions();
  const navigate = useNavigate();
  const securityQuestions = useMemo(() => {
    if (tbhSecurityQuestions?.data) {
      const decrypted = decryptJson(tbhSecurityQuestions?.data);
      const arr = decrypted?.map((item: { question: string }) => ({
        label: item?.question,
        value: item?.question,
      }));
      return arr;
    }
  }, [tbhSecurityQuestions?.data]);

  const formik: any = useFormik({
    initialValues: {
      question1: '',
      question2: '',
      question3: '',
      answer1: '',
      answer2: '',
      answer3: '',
    },
    validationSchema: yup.object({
      question1: VALIDATIONS.generic,
      question2: VALIDATIONS.generic,
      question3: VALIDATIONS.generic,
      answer1: VALIDATIONS.generic,
      answer2: VALIDATIONS.generic,
      answer3: VALIDATIONS.generic,
    }),
    onSubmit: (values) => {
      setUsersSecurityQuestions(
        {
          data: encryptData({
            answer1: CryptoJS.AES.encrypt(formik.values.answer1, wqkgx).toString(),
            answer2: CryptoJS.AES.encrypt(formik.values.answer2, wqkgx).toString(),
            answer3: CryptoJS.AES.encrypt(formik.values.answer3, wqkgx).toString(),
            question1: formik.values.question1,
            question2: formik.values.question2,
            question3: formik.values.question3,
            email,
          }),
          token,
        },
        {
          onSuccess: () => {
            if (action === 'login') {
              navigate('/about/business');
            } else {
              setShowSuccessModal(true);
              signupEmail && localStorage.removeItem('signup-email');
            }
            registerNetcoreActivity(
              {
                emailAddress: email,
                firstName: details && details.firstName,
                lastName: details && details.lastName,
                mobileNumber: details && details.phoneNumber,
              },
              'TBH_SIGN_UP_USER_SET_SECURITYS_QUESTIONS',
            );
            ReactGA.event({
              category: 'Button',
              action: 'TBH_SIGN_UP_USER_SET_SECURITYS_QUESTIONS',
              label: 'TBH_SIGN_UP_USER_SET_SECURITYS_QUESTIONS',
              // value: 1
            });
          },
          onError: (error: Error) => {
            customToast(error?.message || 'Could not set security questions', ToastType.error);
          },
        },
      );
    },
  });

  useEffect(() => {
    if (!isEmailValid(email) || !validateEmail(email)) {
      setDisabled(true);
      customToast(`Your email ${email} is invalid`, ToastType.error);
    } else {
      setDisabled(false);
    }
  }, [email]);

  const updatedSecurityQuestions = useMemo(() => {
    const selectedQuestions = [
      formik.values.question1,
      formik.values.question2,
      formik.values.question3,
    ];
    const arr = securityQuestions?.filter(
      (obj: { label: string }) => !selectedQuestions?.includes(obj?.label),
    );
    return arr;
  }, [formik.values, securityQuestions]);

  return (
    <Box
      bgColor={'white'}
      px={{ base: passwordEmail && '2rem', md: '3.5rem' }}
      py={'2rem'}
      borderRadius={'0.5rem'}
    >
      {hasBack && (
        <Flex
          mb={'2rem'}
          alignItems={'center'}
          gap={'8px'}
          cursor={'pointer'}
          onClick={() => navigate(-1)}
        >
          <Img src={arrowLeft} boxSize={{ base: '1rem', md: '1.5rem' }} />
          <Typography color={Colors.green} fontSize={'0.7rem'} fontWeight={500}>
            Back
          </Typography>
        </Flex>
      )}
      <Box mb={'2rem'}>
        <Typography color={Colors.black} fontSize={'1.125rem'} fontWeight={700} mb={'1rem'}>
          Set Security Questions
        </Typography>
        <Typography color={Colors.black} fontSize={'0.75rem'} fontWeight={400} mb={'1.5rem'}>
          {`Please set your new security questions and provide the answers to each question.`}
        </Typography>
        <Flex direction={'column'}>
          {[1, 2, 3].map((val) => (
            <Box mb={'24px'} key={val}>
              <Dropdown
                options={updatedSecurityQuestions}
                placeholder={formik.values?.[`question${val}`] || `Select security question ${val}`}
                setSelectedOption={(value: { label: string; value: string }) =>
                  formik.setFieldValue(`question${val}`, value?.label)
                }
                borderColor='border-[#D4D9E0] border-[0.5px]'
                placeholderFont='12px'
                placeholderColor='!text-[#212121]'
                isLoading={tbhSecurityQuestionsLoading}
                className='h-[2.875rem] !w-[100%] font-[Poppins]'
              />
              {formik?.touched?.[`question${val}`] && formik?.errors?.[`question${val}`] && (
                <Typography color={'red'} fontSize={'11px'} fontWeight={400}>
                  {formik?.errors?.[`question${val}`]}
                </Typography>
              )}

              <Box position={'relative'} mt={'12px'} w={'100%'}>
                <Input
                  borderRadius={'0.25rem'}
                  border={`0.5px solid ${Colors.secondaryGreen300}`}
                  placeholder='Enter your answer'
                  type={'text'}
                  fontSize={'0.75rem'}
                  fontWeight={400}
                  flex={1}
                  height={'2.8125rem'}
                  onChange={(e) => formik.setFieldValue(`answer${val}`, e.target.value)}
                  _placeholder={{
                    mt: '-10px',
                    color: Colors.black,
                  }}
                  w={'100%'}
                  value={formik.values?.[`answer${val}`]}
                  px={'16px'}
                  _focusVisible={{
                    outlineColor: 'none',
                    border: `2px solid ${Colors.green}`,
                  }}
                  fontFamily={'Poppins'}
                />
                {formik?.touched?.[`answer${val}`] && formik?.errors?.[`answer${val}`] && (
                  <Typography color={'red'} fontSize={'11px'} fontWeight={400}>
                    {formik?.errors?.[`answer${val}`]}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Flex>
      </Box>
      <Button
        width={'100%'}
        onClick={() => formik.handleSubmit()}
        py={{ base: '0.75rem', md: '1rem' }}
        height={{ base: '2.5rem', md: '2.875rem' }}
        text={'SET SECURITY QUESTIONS'}
        type='submit'
        isLoading={settingUsersQuestionLoading}
        zIndex={1000}
        isDisabled={disabled}
        position={'relative'}
      />
      {disabled && (
        <Typography color={'red'} fontSize={'11px'} mt={'10px'} fontWeight={400}>
          {`You have to fix your email to proceed.`}
        </Typography>
      )}

      <SuccessModal
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        pageComingFrom={'Login'}
        proceedText={
          loginToken ? 'Return to dashboard' : passwordToken ? 'Return to forgot password' : 'LOGIN'
        }
        whereTo={loginToken ? '/dashboard' : passwordToken ? '/forgot-password' : '/'}
        description='You have successfully set your security questions'
      />
    </Box>
  );
};

export default SetSecurityQuestions;
